import type { ListParams, PageResult } from '@beonit/adaptive-raindrop';
import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { listQuery } from '@shared/infrastructure/http/list-query.js';
import { formatCount } from '../lib/format-count.js';
import { isControlKind } from '../lib/group-owner.js';
import type {
  AnalysisQueue,
  AnalysisRun,
  EngineVersions,
  QueueHealth,
  QueueSlots,
  ReplayedRun,
  RunConfigRef,
  RunFilters,
  RunPlacement,
  RunStage,
  WaitingRun,
} from '../model.js';

/**
 * Exactly what an analysis run looks like on the wire.
 *
 * Virtual Profiler's own spelling, left untouched: snake_case, `_id`, instants as
 * text. Declaring it here rather than reaching for the model is the whole point of
 * a gateway — this is the only file in the frontend that knows Virtual Profiler
 * names things differently, and the only one that has to change when it renames one.
 *
 * Exported for the other gateways that list runs (the runs of one group execution), which read
 * them through `toRun` rather than a second translation that would drift from this one.
 */
export interface RunPayload {
  _id: string;
  source_type: AnalysisRun['sourceType'];
  source_id: string;
  /** Upstream names each run's source when it can; older builds send nothing. */
  source_name?: string | null;
  /** `transcription` for a video's transcription listed among the runs; see `AnalysisRun.jobType`. */
  job_type?: string | null;
  status: AnalysisRun['status'];
  attempt: number;
  trigger: AnalysisRun['trigger'];
  batch: string | null;
  note: string | null;
  group_id: string | null;
  /** The control whose execution it is; absent from a ledger older than the controls. */
  control_kind?: string | null;
  promote_on_success: boolean;
  position: number | null;
  priority: number;
  enqueued_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  heartbeat_at: string | null;
  /** Absent from ledger builds older than the field; null before the run starts. */
  worker_id?: string | null;
  current_step: string | null;
  progress: number;
  engines_requested: string[];
  engines_auto_added: string[];
  engines_executed: string[];
  engines_skipped: Array<{ name: string; reason: string }>;
  stages: StagePayload[];
  config: ConfigPayload | null;
  engine: EnginePayload | null;
  engine_resolved: EnginePayload | null;
  error: { type: string; message: string; stage: string | null } | null;
  result_doc_id: string | null;
  cancel: CancelPayload | null;
  /**
   * The stored job. The key is absent on a run from before the holding pen, and the lists drop
   * its payload: only whether it is there is read.
   */
  dispatch?: { queue_type?: string } | null;
  run_options?: { promote_on_success?: boolean } | null;
  /** Only on an automatic retry; absent from every other run. */
  retry_of?: string | null;
}

interface StagePayload {
  name: string;
  status: RunStage['status'];
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
}

interface ConfigPayload {
  interview_analysis_config_id: number | null;
  gestures_mongo_config_id: string | null;
  version: number | null;
  engines_hash: string | null;
  /** Only on the single-run endpoint; absent from every list. */
  snapshot?: Record<string, unknown> | null;
}

interface EnginePayload {
  tool_version: string;
  build_sha: string;
  engines: Record<string, string>;
  resolved: Record<string, unknown>;
  silent_fallbacks: Array<{ name: string; detail: string; at: string }>;
  degraded: boolean;
  degraded_reasons: string[];
}

interface CancelPayload {
  requested_at: string;
  reason: string | null;
  forced: boolean;
  ignored_because_finishing: boolean;
}

interface QueuePayload {
  waiting: Array<RunPayload & { queue_index: number | null }>;
  running: RunPayload[];
  recent: RunPayload[];
  slots: {
    capacity: number | null;
    in_use: number;
    free: number | null;
    source: QueueSlots['source'];
  };
  /** Absent from Virtual Profiler builds older than the field. */
  workers?: Array<{
    name: string;
    concurrency: number;
    active: string[];
    versions?: {
      tool_version: string | null;
      build_sha: string | null;
      engines: Record<string, string>;
    } | null;
  }>;
  paused: boolean;
  health: {
    bridge_alive: boolean;
    last_message_at: string | null;
    degraded_runs: number;
    /**
     * Optional because it is the newest field of the block: a Virtual Profiler deployed before
     * it existed answers without it.
     */
    failed_runs?: number;
    lost_runs: number;
    oldest_queued_at: string | null;
    storage: {
      collections: Record<string, { documents: number | null; size_bytes: number | null }>;
      total_size_bytes: number | null;
      orphan_chunks: number;
    } | null;
  };
  versions: {
    api: { tool_version?: string | null; build_sha?: string | null };
    worker: EnginePayload | null;
  };
}

/** The one place a date is born in this module. */
function date(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

function toStage(payload: StagePayload): RunStage {
  return {
    name: payload.name,
    status: payload.status,
    startedAt: date(payload.started_at),
    finishedAt: date(payload.finished_at),
    durationMs: payload.duration_ms,
  };
}

function toConfig(payload: ConfigPayload | null): RunConfigRef | null {
  if (!payload) return null;

  return {
    enginesHash: payload.engines_hash,
    interviewAnalysisConfigId: payload.interview_analysis_config_id,
    gesturesConfigId: payload.gestures_mongo_config_id,
    version: payload.version,
    // Absent on a list and null on a pre-versioning run: two different absences, kept apart so
    // a diff can say «no se grabó nada» rather than «no cambió nada».
    snapshot: payload.snapshot ?? null,
  };
}

function toEngine(payload: EnginePayload | null): EngineVersions | null {
  if (!payload) return null;

  return {
    toolVersion: payload.tool_version,
    buildSha: payload.build_sha,
    engines: payload.engines ?? {},
    resolved: payload.resolved ?? {},
    silentFallbacks: (payload.silent_fallbacks ?? []).map((fallback) => ({
      name: fallback.name,
      detail: fallback.detail,
      // Never null: a fallback that happened happened at some moment.
      at: new Date(fallback.at),
    })),
    degraded: payload.degraded,
    degradedReasons: payload.degraded_reasons ?? [],
  };
}

export function toRun(payload: RunPayload): AnalysisRun {
  return {
    id: payload._id,
    sourceType: payload.source_type,
    sourceId: payload.source_id,
    sourceName: payload.source_name ?? null,
    status: payload.status,
    attempt: payload.attempt,
    trigger: payload.trigger,
    batch: payload.batch,
    note: payload.note,
    groupId: isControlKind(payload.control_kind) ? null : payload.group_id,
    controlKind: isControlKind(payload.control_kind) ? payload.control_kind : null,
    promoteOnSuccess: payload.promote_on_success,
    position: payload.position,
    priority: payload.priority,
    enqueuedAt: date(payload.enqueued_at),
    startedAt: date(payload.started_at),
    finishedAt: date(payload.finished_at),
    heartbeatAt: date(payload.heartbeat_at),
    workerId: payload.worker_id ?? null,
    currentStep: payload.current_step,
    progress: payload.progress,
    enginesRequested: payload.engines_requested ?? [],
    enginesAutoAdded: payload.engines_auto_added ?? [],
    enginesExecuted: payload.engines_executed ?? [],
    enginesSkipped: payload.engines_skipped ?? [],
    stages: (payload.stages ?? []).map(toStage),
    config: toConfig(payload.config),
    // `engine_resolved` is what the worker actually used and `engine` what was
    // planned. When both are there the resolved one is the truth; a screen showing
    // the plan would claim versions that never ran.
    engine: toEngine(payload.engine_resolved ?? payload.engine),
    error: payload.error,
    resultId: payload.result_doc_id,
    cancellation: payload.cancel
      ? {
          requestedAt: new Date(payload.cancel.requested_at),
          reason: payload.cancel.reason,
          forced: payload.cancel.forced,
          ignoredBecauseFinishing: payload.cancel.ignored_because_finishing,
        }
      : null,
    hasStoredJob: Boolean(payload.dispatch),
    replayPromotes: payload.run_options?.promote_on_success === true,
    retryOf: payload.retry_of ?? null,
    jobType: payload.job_type ?? null,
  };
}

function toWaitingRun(payload: RunPayload & { queue_index: number | null }): WaitingRun {
  return { ...toRun(payload), queueIndex: payload.queue_index };
}

function runPath(runId: string, action: string): string {
  return `/virtual-profiler/analysis-runs/${encodeURIComponent(runId)}/${action}`;
}

function toHealth(payload: QueuePayload['health']): QueueHealth {
  return {
    bridgeAlive: payload.bridge_alive,
    lastMessageAt: date(payload.last_message_at),
    degradedRuns: payload.degraded_runs,
    // Zero and not null when it is absent: the strip renders a digit here, and «—» in a tile
    // reading «Con error» would look like a screen that failed rather than a queue with nothing
    // wrong. The window where it can be absent is a Virtual Profiler older than this build.
    failedRuns: payload.failed_runs ?? 0,
    lostRuns: payload.lost_runs,
    oldestQueuedAt: date(payload.oldest_queued_at),
    storage: payload.storage
      ? {
          // A map keyed by collection name becomes a list: the screen renders rows
          // and a map has no order, so it would render them differently each time.
          collections: Object.entries(payload.storage.collections ?? {}).map(([name, stats]) => ({
            name,
            documents: stats.documents,
            sizeBytes: stats.size_bytes,
          })),
          totalSizeBytes: payload.storage.total_size_bytes,
          orphanChunks: payload.storage.orphan_chunks,
        }
      : null,
  };
}

function toQueue(payload: QueuePayload): AnalysisQueue {
  return {
    waiting: (payload.waiting ?? []).map(toWaitingRun),
    running: (payload.running ?? []).map(toRun),
    recent: (payload.recent ?? []).map(toRun),
    slots: {
      capacity: payload.slots.capacity,
      inUse: payload.slots.in_use,
      free: payload.slots.free,
      source: payload.slots.source,
    },
    // Null, not empty, from a build that does not send them: «no workers» is an answer, and
    // the timeline must not read a missing field as one.
    workers: payload.workers
      ? payload.workers.map((worker) => ({
          name: worker.name,
          concurrency: worker.concurrency,
          active: worker.active ?? [],
          versions: worker.versions
            ? {
                toolVersion: worker.versions.tool_version ?? null,
                buildSha: worker.versions.build_sha ?? null,
                engines: { ...worker.versions.engines },
              }
            : null,
        }))
      : null,
    paused: payload.paused,
    health: toHealth(payload.health),
    versions: {
      api: {
        toolVersion: payload.versions?.api?.tool_version ?? null,
        buildSha: payload.versions?.api?.build_sha ?? null,
      },
      worker: toEngine(payload.versions?.worker ?? null),
    },
  };
}

/** The largest page the gateway takes: «todas» in as few requests as it allows. */
const ALL_RUNS_PAGE_SIZE = 200;

/** How many runs «seleccionar todas» gathers before refusing. */
export const ALL_RUNS_LIMIT = 1000;

/**
 * «Todas» refused because there are too many.
 *
 * Its own class, and its message is what the operator reads: the way out is a narrower filter, and
 * the generic «no se ha podido completar» would read as something broken.
 */
export class TooManyRunsError extends Error {
  constructor(readonly total: number) {
    super(`Hay ${formatCount(total)} ejecuciones: filtra un poco más para seleccionarlas todas`);
    this.name = 'TooManyRunsError';
  }
}

async function readHistory(
  params: ListParams,
  filters: RunFilters = {},
): Promise<PageResult<AnalysisRun>> {
  const page = await httpClient.getPage<RunPayload>(
    `/virtual-profiler/analysis-runs${listQuery(params, {
      sourceType: filters.sourceType,
      sourceId: filters.sourceId,
      groupId: filters.groupId,
      controlKind: filters.controlKind,
      status: filters.status,
      include: filters.includeTranscriptions ? 'transcriptions' : undefined,
    })}`,
  );

  return { items: page.items.map(toRun), meta: page.meta };
}

/** The most runs one bulk withdraw or cancel takes: the back-office's cap. */
const BULK_RUNS_MAX = 500;

/** One call per `BULK_RUNS_MAX` runs, the outcomes joined: any selection on screen fits in one. */
async function inRunChunks(
  runIds: readonly string[],
  send: (chunk: readonly string[]) => Promise<BulkRunOutcome[] | null | undefined>,
): Promise<BulkRunOutcome[]> {
  const outcomes: BulkRunOutcome[] = [];
  for (let start = 0; start < runIds.length; start += BULK_RUNS_MAX) {
    outcomes.push(...((await send(runIds.slice(start, start + BULK_RUNS_MAX))) ?? []));
  }
  return outcomes;
}

/** One run of a bulk withdraw or cancel, as the back-office answers it (2026-10-29). */
export interface BulkRunOutcome {
  run_id: string;
  outcome: 'done' | 'conflict' | 'missing' | 'failed';
  code: string | null;
  reason: string | null;
}

/**
 * Boundary with the analysis queue and the run history.
 *
 * Every path here goes through the back-office, never straight to Virtual Profiler:
 * the permission is checked there and the browser has no key of its own.
 */
export const analysisRunsApi = {
  /** The whole queue screen in one request, which is why it is the cheapest one. */
  queue: async (): Promise<AnalysisQueue> =>
    toQueue(await httpClient.get<QueuePayload>('/virtual-profiler/analysis-runs/queue')),

  history: readHistory,

  /**
   * Every run the filters match, across all the pages, for «seleccionar todas».
   *
   * Whole rows and not only ids: a bulk action decides per run whether it is withdrawn or cancelled,
   * and that is the run's status, which a bare id does not carry. Walked at the largest page the
   * gateway takes and stopped on an empty page too, so a total that moves underneath cannot turn it
   * into a loop; a run seen twice — the list shifts as runs are launched — is kept once.
   *
   * Refused past `ALL_RUNS_LIMIT`, before a second request: selecting thousands of runs to act on
   * them one request at a time is a mistake the filters should have prevented, not a wait.
   */
  allMatching: async (filters: RunFilters = {}): Promise<AnalysisRun[]> => {
    const runs = new Map<string, AnalysisRun>();

    for (let page = 1; ; page += 1) {
      const answer = await readHistory({ page, pageSize: ALL_RUNS_PAGE_SIZE }, filters);

      if (answer.meta.total > ALL_RUNS_LIMIT) throw new TooManyRunsError(answer.meta.total);

      for (const run of answer.items) runs.set(run.id, run);

      if (answer.items.length === 0 || page >= answer.meta.totalPages) return [...runs.values()];
    }
  },

  get: async (runId: string): Promise<AnalysisRun> =>
    toRun(
      await httpClient.get<RunPayload>(
        `/virtual-profiler/analysis-runs/${encodeURIComponent(runId)}`,
      ),
    ),

  /**
   * Takes a run out of the queue before it starts.
   *
   * «Quitar de la cola», never «borrar». The endpoint is a POST to `withdraw` for
   * the same reason: nothing in this module erases a finished analysis, and a verb
   * that reads like it does is the likeliest misunderstanding on that screen.
   */
  withdraw: (runId: string): Promise<void> =>
    httpClient.post<void>(`/virtual-profiler/analysis-runs/${encodeURIComponent(runId)}/withdraw`),

  /** Asks a running analysis to stop. It lands at the next checkpoint, not at once. */
  cancel: (runId: string): Promise<void> =>
    httpClient.post<void>(`/virtual-profiler/analysis-runs/${encodeURIComponent(runId)}/cancel`),

  /**
   * Several runs out of the queue in one call (2026-10-29), each answered as its own route would:
   * `done`, `conflict` (it started meanwhile), `missing` or `failed`.
   */
  withdrawMany: (runIds: readonly string[]): Promise<BulkRunOutcome[]> =>
    inRunChunks(runIds, (chunk) =>
      httpClient.post<BulkRunOutcome[]>('/virtual-profiler/analysis-runs/bulk-withdraw', {
        runIds: chunk,
      }),
    ),

  /** Several running analyses asked to stop in one call; each answered as `withdrawMany`. */
  cancelMany: (runIds: readonly string[]): Promise<BulkRunOutcome[]> =>
    inRunChunks(runIds, (chunk) =>
      httpClient.post<BulkRunOutcome[]>('/virtual-profiler/analysis-runs/bulk-cancel', {
        runIds: chunk,
      }),
    ),

  pauseQueue: (): Promise<void> =>
    httpClient.post<void>('/virtual-profiler/analysis-runs/queue/pause'),

  resumeQueue: (): Promise<void> =>
    httpClient.post<void>('/virtual-profiler/analysis-runs/queue/resume'),

  /**
   * Moves a waiting run within its lane.
   *
   * `RunPlacement` is already the body the endpoint takes — exactly one of
   * `beforeRunId`, `afterRunId` or `position`, which is the union's whole job.
   */
  move: (runId: string, placement: RunPlacement): Promise<void> =>
    httpClient.post<void>(
      `/virtual-profiler/analysis-runs/${encodeURIComponent(runId)}/position`,
      placement,
    ),

  /**
   * Launches a finished run again: same engines, configuration and stored job.
   *
   * It cannot add coherence to a gestures-only run, and it promotes only when the original was
   * going to. Absent labels reuse the original run's, so only what was typed travels.
   */
  replay: async (
    runId: string,
    labels: { batch?: string; note?: string } = {},
  ): Promise<ReplayedRun> => {
    const body: Record<string, string> = {};

    if (labels.batch?.trim()) body.batch = labels.batch.trim();
    if (labels.note?.trim()) body.note = labels.note.trim();

    const answer = await httpClient.post<{
      run_id: string;
      replay_of: string;
      position: number | null;
      engines: string[];
    }>(runPath(runId, 'replay'), body);

    return {
      runId: answer.run_id,
      replayOf: answer.replay_of,
      position: answer.position ?? null,
      engines: answer.engines ?? [],
    };
  },
};
